import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo, useEffect } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/venue/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import {
  fromSpecialHoursInputEntriesToSpecialHours,
  fromSpecialHoursToSpecialHoursInputEntries,
  SpecialHoursInputEntry,
} from 'common/components/weekly-schedules';

import SpecialHoursForm from './SpecialHoursForm';
import SpecialHoursView from './SpecialHoursView';
import { reportSpecialHoursEdit, reportSpecialHoursEditCancel } from '../../behavioral';

type Props = {
  className?: string;
  editable?: boolean;
  venue: Venue;
};

type InnerProps = Props & InjectedIntlProps;

const Root = styled.div`
  position: relative;
  margin-block-end: 32px;
  padding-block-end: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const SpecialHours = ({ className, editable, intl, venue }: InnerProps) => {
  const queryClient = useQueryClient();
  const t = (id: string) => intl.formatMessage({ id });

  const [editing, setEditing] = useState(false);

  const specialHoursEntries = useMemo(
    () =>
      venue.exceptional_opening_times.length
        ? fromSpecialHoursToSpecialHoursInputEntries(venue.exceptional_opening_times, venue.timezone)
        : [],
    [venue],
  );

  const specialHoursMutation = useMutation(
    (specialHours: SpecialHoursInputEntry[]) => {
      const exceptionalOpeningTimes = fromSpecialHoursInputEntriesToSpecialHours(
        specialHours,
        venue.timezone,
        venue.opening_times,
      );
      return api.editExceptionalOpeningTimes(venue.id, exceptionalOpeningTimes);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['/v1/merchant-admin/venues/', venue.slug]);
      },
      onSettled: () => {
        reportSpecialHoursEdit();
      },
    },
  );

  useEffect(() => {
    if (specialHoursMutation.isSuccess) {
      setEditing(false);
    }
  }, [specialHoursMutation.isSuccess]);

  const handleEditToggle = () => setEditing(true);

  const handleCancel = () => {
    setEditing(false);
    reportSpecialHoursEditCancel();
  };

  const handleSubmit = async ({ specialHours }: { specialHours: SpecialHoursInputEntry[] }) => {
    specialHoursMutation.mutate(specialHours);
  };

  return (
    <Root className={className}>
      {editing ? (
        <SpecialHoursForm
          entries={specialHoursEntries}
          timezone={venue.timezone}
          submitting={specialHoursMutation.isLoading}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      ) : (
        <SpecialHoursView
          entries={specialHoursEntries}
          onEdit={editable ? handleEditToggle : undefined}
          warning={specialHoursMutation.isError ? t('special-hours.submit-had-errors') : undefined}
        />
      )}
    </Root>
  );
};

export default injectIntl(SpecialHours);
